import { api } from "./api";
import { config } from "./config";

// F9 ガイド登録/保険 API の型付きラッパ（apps/api/src/guides.ts・insurance.ts と対）。

// ── POST /guides（ガイド登録）─────────────────────────────────────────
// email は JWT(email クレーム)を正とするため body には載せない（なりすまし防止 / guides.ts:73）。
export type RegisterGuideInput = {
  fullName: string;
  phone?: string;
  bank?: {
    name?: string;
    branch?: string;
    accountType?: "Savings" | "Checking";
    accountNumber?: string;
    accountHolder?: string;
  };
  emergencyContacts?: Array<{ name: string; phone: string; relationship?: string }>;
  languages?: string[];
  /** 対応エリア。9 都市（TOUR_CITIES）から 1 つ以上が必須（tst-szv6.32）。 */
  serviceAreas: string[];
  consents?: Array<{ documentType: string; documentVersion: string }>;
};

export type RegisterGuideResult = { id: string; status: string };

/**
 * v1 の言語は English 固定運用。マッチング候補条件（guides.ts / matching-eligibility.ts）が
 * guide_language に English を要求するため、オンボーディングは必ずこれを送る（tst-00cu.9）。
 */
export const DEFAULT_GUIDE_LANGUAGE = "English";

/**
 * 対応エリアの選択肢（9 都市）。予約の都市（customer-web Step1）と同じ値集合で、マッチングの
 * 必須条件が「予約の都市がガイドの対応エリアに含まれること」を見る（apps/api tour-cities.ts と対）。
 */
export const TOUR_CITIES = [
  "Tokyo",
  "Kyoto",
  "Osaka",
  "Nara",
  "Kobe",
  "Hiroshima",
  "Kanazawa",
  "Nagasaki",
  "Fukuoka",
] as const;

/** ガイド本登録。guide 行 + 銀行 + 緊急連絡先 + 同意ログを単一 TX で作成し status=Onboarding を返す。 */
export function registerGuide(input: RegisterGuideInput): Promise<RegisterGuideResult> {
  return api<RegisterGuideResult>("/guides", { method: "POST", body: input });
}

// ── POST /guides/:guideId/insurance（保険証券 presigned PUT 発行）──────
export type InsuranceRequestInput = {
  insurer?: string;
  policyNumber?: string;
  validFrom?: string; // YYYY-MM-DD
  validUntil?: string; // YYYY-MM-DD
  contentType?: string; // 既定 application/pdf
};

export type InsuranceRequestResult = {
  policyId: string;
  s3Key: string;
  uploadUrl: string;
  expiresInSeconds: number;
};

/** 保険証券アップロード用の presigned PUT URL を発行（insurance_policy を Pending で記録）。 */
export function requestInsuranceUpload(
  guideId: string,
  input: InsuranceRequestInput,
): Promise<InsuranceRequestResult> {
  return api<InsuranceRequestResult>(`/guides/${guideId}/insurance`, {
    method: "POST",
    body: input,
  });
}

/**
 * presigned URL に保険証券 PDF を直接 PUT する（S3 はバケット既定 SSE-KMS で暗号化）。
 * api ラッパは通さない（S3 への直アップロードで authorization ヘッダ不要・付けると署名不一致になる）。
 */
export async function uploadInsuranceFile(
  uploadUrl: string,
  file: File | Blob,
  contentType = "application/pdf",
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "content-type": contentType },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`S3 upload failed (${res.status})`);
  }
}

// ── GET /guides/me/status（自分の登録状態 / tst-00cu.5）─────────────────
// ログイン後・提出後の居場所を決めるためだけの軽量な口。PII は返さない。
export type MyGuideStatus = {
  guideId: string;
  /** guide.status（Applying / Onboarding / Active / Inactive / Terminated）。 */
  status: string;
  /** 最後に出した保険証券の審査状態（Pending / Approved / Rejected）。未提出なら null。期限は返さない。 */
  insurance: { verificationStatus: string } | null;
  /** 承認時に生成される契約（最新版）。承認前は null。 */
  contract: { status: string; validUntil: string | null } | null;
};

/**
 * 自分の登録状態。cognito_sub にバインドされた guide 行が無ければ 404 guide_not_registered
 * （＝まだ本登録ウィザードを終えていない）。退会・休止・業務停止は session-guard の 403 guide_inactive。
 */
export function fetchMyGuideStatus(): Promise<MyGuideStatus> {
  return api<MyGuideStatus>("/guides/me/status");
}

// ── GET /guides/me/requirements（本登録で求める書類の要否 / tst-szv6.5）──────
// 保険証券の提出は管理画面の設定（Settings → Guide insurance requirement）で保留にできる。既定は保留
// （insuranceRequired=false）。/guides/me/status と別の口なのは、ウィザードが出るのが「guide 行が
// まだ無い」（status が 404）ときだからで、あちらの応答に載せても未登録の人には届かない。
export type GuideRequirements = {
  /** 保険証券の提出を求めるか。false の間は保険のステップ・状態表示を出さない。 */
  insuranceRequired: boolean;
};

export function fetchGuideRequirements(): Promise<GuideRequirements> {
  return api<GuideRequirements>("/guides/me/requirements");
}

// stg/prod 既定接続先の表示用（g-01 デバッグ・サポート導線）。
export const apiBaseUrl = config.apiBaseUrl;

// ── POST /guide-device-reports（G-03-M01 端末紛失申告 / F-GUIDE-032）─────
// サインイン未完了（JWT 未取得）の状態から叩くため公開ルート。api() は token 未登録時は
// Authorization ヘッダを付けないだけなので、サインイン後でも支障なく再利用できる。
export type DeviceReportInput = {
  email: string;
  issueType?: "lost_device" | "compromised_account" | "other";
  details?: string;
};

export type DeviceReportResult = { status: string; message: string };

/**
 * 端末紛失/アカウント侵害を申告する。バックエンドは apps/api/src/guide-sessions.ts
 * （guideDeviceReports）で email 突合し、該当ガイドの全セッションを即時失効する。
 * 列挙防止のためレスポンスは email 一致有無に関わらず一律。
 */
export function reportLostDevice(input: DeviceReportInput): Promise<DeviceReportResult> {
  return api<DeviceReportResult>("/guide-device-reports", { method: "POST", body: input });
}
